import { gql } from '@apollo/client';

/** Reach (Calendar · Campaigns · Channels): queries, channel names and dates. */

export const REACH_POST_FIELDS = `
  id
  platform
  summary
  title
  imageUrl
  reachStatus
  failure
  reviewStatus
  scheduleTime
  proposedTime
  socialPublishDate
  socialProfileId
`;

export const REACH_CALENDAR = gql`
  query ReachCalendar($accountId: ID!, $from: String!, $to: String!) {
    reachCalendar(accountId: $accountId, from: $from, to: $to) {
      posts { ${REACH_POST_FIELDS} }
      waiting { ${REACH_POST_FIELDS} }
      drafts { ${REACH_POST_FIELDS} }
    }
  }
`;

/** Calendar's "New on your site, not shared yet" (apiserver reach/services/unshared.js). */
export const REACH_UNSHARED = gql`
  query ReachUnshared($accountId: ID!) {
    reachUnshared(accountId: $accountId) {
      articles {
        id
        title
        link
        publishedAt
      }
      channels {
        id
        platform
        name
      }
      sharingPaused
    }
  }
`;

/** One waiting post per new article per channel: nothing posts until approved. */
export const DRAFT_POSTS_FOR_NEW_ARTICLES = gql`
  mutation DraftPostsForNewArticles($accountId: ID!) {
    draftPostsForNewArticles(accountId: $accountId) {
      drafted
      skipped
      articles
    }
  }
`;

/** "X and Threads", "X, Threads and LinkedIn". */
export function andList(items: string[]): string {
  return items.length < 2
    ? items.join('')
    : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/** A draft goes on the calendar: "now" (in a minute) or an ISO time ahead. */
export const SCHEDULE_POST_DRAFT = gql`
  mutation ScheduleSocialPostDraft($id: ID!, $scheduleTime: String!) {
    scheduleSocialPostDraft(id: $id, scheduleTime: $scheduleTime) { ${REACH_POST_FIELDS} }
  }
`;

export const DISCARD_POST_DRAFT = gql`
  mutation DiscardSocialPostDraft($id: ID!) {
    discardSocialPostDraft(id: $id) { ${REACH_POST_FIELDS} }
  }
`;

export const APPROVE_SOCIAL_POST = gql`
  mutation ApproveSocialPost($id: ID!) {
    approveSocialPost(id: $id) { ${REACH_POST_FIELDS} }
  }
`;

/** A post that didn't go out goes out again in a minute ("Try again"). */
export const RETRY_SOCIAL_POST = gql`
  mutation RetrySocialPost($id: ID!) {
    retrySocialPost(id: $id) { ${REACH_POST_FIELDS} }
  }
`;

/** Undo of Skip / Try again on a missed post: missed again at its old time. */
export const RESTORE_MISSED_SOCIAL_POST = gql`
  mutation RestoreMissedSocialPost($id: ID!, $scheduleTime: String!, $reviewStatus: String) {
    restoreMissedSocialPost(id: $id, scheduleTime: $scheduleTime, reviewStatus: $reviewStatus) { ${REACH_POST_FIELDS} }
  }
`;

export const SKIP_SOCIAL_POST = gql`
  mutation SkipSocialPost($id: ID!) {
    skipSocialPost(id: $id) { ${REACH_POST_FIELDS} }
  }
`;

export const APPROVE_CAMPAIGN = gql`
  mutation ApproveCampaign($id: ID!) {
    approveCampaign(id: $id) {
      approved
      scheduled
      undated
      skippedChannels
    }
  }
`;

export const REACH_CHANNELS = gql`
  query ReachChannels($accountId: ID!) {
    socialProfiles(accountId: $accountId) {
      id
      platform
      profileId
      profileName
      profileUsername
      profileLink
      isActive
      isPublishing
      approvalMode
    }
  }
`;

/** Reach → Channels: the channels with each one's last post that has an outcome. */
export const REACH_CHANNEL_ROWS = gql`
  query ReachChannelRows($accountId: ID!) {
    socialProfiles(accountId: $accountId) {
      id
      platform
      profileId
      profileName
      profileUsername
      profileLink
      isActive
      isPublishing
      approvalMode
      lastPost { ${REACH_POST_FIELDS} }
      missedPosts {
        count
        latest { ${REACH_POST_FIELDS} }
      }
      stats {
        followers
        change30d
        asOf
      }
    }
  }
`;

/** Connect the channels picked from a sign-in (the ticket came from the API's exchange). */
export const CONNECT_SOCIAL_CHANNELS = gql`
  mutation ConnectSocialChannels($input: ConnectSocialChannelsInput!) {
    connectSocialChannels(input: $input) {
      id
      platform
      profileId
      profileName
      profileUsername
      profileLink
      isActive
      isPublishing
      approvalMode
    }
  }
`;

/**
 * Reach → Channels "Disconnect": the stored credentials are removed; the row
 * stays, inactive, so its posts keep their channel. (Settings → Social's
 * "Active" switch is a pause through UPDATE_SOCIAL_PROFILE, credentials kept.)
 */
export const DISCONNECT_SOCIAL_CHANNEL = gql`
  mutation DisconnectSocialChannel($id: ID!) {
    disconnectSocialChannel(id: $id) {
      id
      isActive
      isPublishing
    }
  }
`;

/** A person's own post (Reach → Calendar → Write a post): one call per channel. */
export const CREATE_REACH_POST = gql`
  mutation CreateReachPost($input: CreateSocialPostInput!) {
    createSocialPost(input: $input) { ${REACH_POST_FIELDS} }
  }
`;

export const SET_APPROVAL_MODE = gql`
  mutation SetSocialProfileApprovalMode($id: ID!, $mode: String!) {
    setSocialProfileApprovalMode(id: $id, mode: $mode) {
      id
      approvalMode
    }
  }
`;

export interface ReachPost {
  id: string;
  platform: string;
  summary?: string | null;
  title?: string | null;
  /** The post's photo, when it has one (Today shows it small beside the caption). */
  imageUrl?: string | null;
  reachStatus: string;
  /** Why a missed post didn't go out, a clause in plain words (missed posts only). */
  failure?: string | null;
  reviewStatus?: string | null;
  scheduleTime?: string | null;
  proposedTime?: string | null;
  socialPublishDate?: string | null;
  socialProfileId: string;
}

/** An always-on campaign's conditions (apiserver reach/services/alwaysOn.js). */
export interface AlwaysOnConditions {
  priority?: number;
  postType?: 'auto' | 'link' | 'text' | 'photo' | 'video' | 'random' | string;
  maxAgeHours?: number;
  delayHours?: number;
  immediate?: boolean;
  schedule?: Array<{ time?: string; minuteMark?: number; intervalMinutes?: number }>;
  windows?: Array<{
    dayOfWeek: number;
    startTime: string;
    endTime: string;
    spansMidnight?: boolean;
  }>;
  include?: string[];
  exclude?: string[];
  mediaTypes?: string[];
  /** Only on campaigns migrated from the old rules; read-only. */
  filterSql?: string;
}

export interface Channel {
  id: string;
  platform: string;
  profileId?: string | null;
  profileName?: string | null;
  profileUsername?: string | null;
  profileLink?: string | null;
  isActive: boolean;
  isPublishing?: boolean | null;
  approvalMode: 'ask' | 'auto' | string;
  /** The last post with an outcome (REACH_CHANNEL_ROWS only). */
  lastPost?: ReachPost | null;
  /** Unresolved posts that didn't go out, last 30 days, as Today counts them. */
  missedPosts?: { count: number; latest?: ReachPost | null } | null;
  /** The engine's daily follower snapshot (REACH_CHANNEL_ROWS only); null when none. */
  stats?: { followers: number; change30d?: number | null; asOf: string } | null;
}

/**
 * Followers and 30 days for a channel row (Prototype v14): "1,520" and "+120",
 * "—" where the platform gives no count, nothing has been collected yet, or
 * there is no snapshot from 30 days back. A rise is green, as the design shows.
 */
export function channelStatCells(stats: Channel['stats']): {
  followers: string;
  growth: string;
  growthTone: 'up' | 'flat' | 'down' | 'none';
} {
  if (!stats) return { followers: '—', growth: '—', growthTone: 'none' };
  const change = stats.change30d;
  return {
    followers: stats.followers.toLocaleString('en-US'),
    growth:
      change == null
        ? '—'
        : change > 0
          ? `+${change.toLocaleString('en-US')}`
          : change < 0
            ? `−${Math.abs(change).toLocaleString('en-US')}`
            : '0',
    growthTone: change == null ? 'none' : change > 0 ? 'up' : change < 0 ? 'down' : 'flat',
  };
}

/**
 * A channel's health (Prototype v7): "Last post didn't go out · Oct 17", or
 * "3 posts didn't go out · latest Oct 15" when several are still unresolved —
 * the posts Today lists as missed, even when a later one went out. Null when
 * none is missed. The post Fix opens is postToFix().
 */
export function lastPostProblem(
  c: Pick<Channel, 'lastPost' | 'missedPosts'>,
  now = new Date()
): string | null {
  const missed = c.missedPosts;
  if (missed && missed.count > 0 && missed.latest) {
    const at = shortDate(postTime(missed.latest), now);
    if (missed.count === 1) {
      const last = c.lastPost?.id === missed.latest.id;
      const head = last ? 'Last post didn’t go out' : 'A post didn’t go out';
      return at ? `${head} · ${at}` : head;
    }
    return `${missed.count} posts didn’t go out${at ? ` · latest ${at}` : ''}`;
  }
  // An older server without missedPosts: the last post's own outcome.
  if (!missed && c.lastPost?.reachStatus === 'missed') {
    const at = shortDate(postTime(c.lastPost), now);
    return at ? `Last post didn’t go out · ${at}` : 'Last post didn’t go out';
  }
  return null;
}

/** The post Fix opens: the latest one that didn't go out. */
export const postToFix = (c: Pick<Channel, 'lastPost' | 'missedPosts'>): ReachPost | null =>
  c.missedPosts?.latest || (c.lastPost?.reachStatus === 'missed' ? c.lastPost : null);

/** Platforms we can post to, in the order Add a channel shows them. */
export const POSTABLE = [
  'x',
  'threads',
  'instagram',
  'facebook',
  'linkedin',
  'youtube',
  'tiktok',
] as const;

const NAMES: Record<string, [string, string]> = {
  x: ['X', 'X'],
  twitter: ['X', 'X'],
  threads: ['Threads', '@'],
  instagram: ['Instagram', 'IG'],
  facebook: ['Facebook', 'f'],
  youtube: ['YouTube', 'YT'],
  tiktok: ['TikTok', 'TT'],
  linkedin: ['LinkedIn', 'in'],
  snapchat: ['Snapchat', 'SC'],
  bluesky: ['Bluesky', 'B'],
};
export const channelName = (p: string) => NAMES[p?.toLowerCase()]?.[0] || p;
/**
 * What kind of account a channel posts as (Prototype v13): a Facebook Page, a
 * LinkedIn member's own profile, a YouTube channel, otherwise an account.
 */
const KINDS: Record<string, string> = {
  facebook: 'Page',
  linkedin: 'Personal profile',
  youtube: 'Channel',
};
export const channelKind = (p: string) => KINDS[p?.toLowerCase()] || 'Account';
export const isPersonalProfile = (p: string) => channelKind(p) === 'Personal profile';

/** "America/Los_Angeles (PDT)": the zone a picked time is read in. */
export function localZone(now = new Date()): string {
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  const short =
    new Intl.DateTimeFormat([], { timeZoneName: 'short' })
      .formatToParts(now)
      .find((p) => p.type === 'timeZoneName')?.value || '';
  return [zone.replace(/_/g, ' '), short && `(${short})`].filter(Boolean).join(' ');
}

export const channelMark = (p: string) =>
  NAMES[p?.toLowerCase()]?.[1] || p?.[0]?.toUpperCase() || '?';

/** Dates arrive as ISO strings or epoch milliseconds (as strings). */
export function toDate(v?: string | null): Date | null {
  if (!v) return null;
  const d = /^\d{10,}$/.test(v) ? new Date(Number(v)) : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "Jan 13", or "Jan 13, 2025" when it isn't this year. */
/**
 * A campaign's end date (projects.deadline, a DATE) as that calendar day here.
 * The API sends a DATE as UTC midnight ("2026-10-31T00:00:00.000Z"), which in
 * Los Angeles is the evening before — read its UTC day, never the local one.
 */
export function calendarDate(v?: string | null): Date | null {
  const d = toDate(v);
  return d ? new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) : null;
}

/** A picked day as the DATE the API stores ("2026-10-31"), never shifted by a zone. */
export function dateValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * A campaign brief's opening as one plain line for a card: markup, markdown
 * and the agent's all-caps section labels ("STRATEGY OVERVIEW", "Solution
 * Description:") dropped.
 */
export function briefExcerpt(raw?: string | null, max = 160): string {
  const text = String(raw || '')
    .replace(/<[^>]+>/g, ' ')
    .split(/\r?\n/)
    .map((l) =>
      l
        .replace(/^[#>*\-\s]+/, '')
        .replace(/[*_`]+/g, '')
        .trim()
    )
    .filter((l) => l && !/^[A-Z0-9 &/,'’-]{3,}:?$/.test(l))
    .map((l) => l.replace(/^[A-Z][\w ’'-]{0,40}:\s*/, ''))
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
  return text.length > max ? `${text.slice(0, max).replace(/\s+\S*$/, '')}…` : text;
}

export function shortDate(v?: string | Date | null, now = new Date()): string {
  const d = v instanceof Date ? v : toDate(v);
  if (!d) return '';
  return d.toLocaleDateString(
    [],
    d.getFullYear() === now.getFullYear()
      ? { month: 'short', day: 'numeric' }
      : { month: 'short', day: 'numeric', year: 'numeric' }
  );
}

/**
 * A dated campaign's run, from the campaign itself (never from its posts):
 * created → deadline, the same range on Reach → Campaigns and on Today.
 * Campaigns have no separate start date; the created date is where it starts.
 * The year shows when it isn't this year: "Jan 13 – Jan 18, 2025" (one year),
 * "Dec 20, 2025 – Jan 4" (across years).
 */
export function campaignRange(
  c: { createdAt?: string | null; deadline?: string | null },
  now = new Date()
): string {
  const start = toDate(c.createdAt);
  const end = calendarDate(c.deadline);
  if (start && end && start.getFullYear() === end.getFullYear()) {
    const day = (d: Date) => d.toLocaleDateString([], { month: 'short', day: 'numeric' });
    return `${day(start)} – ${shortDate(end, now)}`;
  }
  if (start && end) return `${shortDate(start, now)} – ${shortDate(end, now)}`;
  return shortDate(start || end, now);
}

/** When the calendar places a post: when it went out, will go out, or is proposed to. */
export const postTime = (p: ReachPost) =>
  toDate(p.socialPublishDate) || toDate(p.scheduleTime) || toDate(p.proposedTime);

export const postText = (p: ReachPost) => (p.summary || p.title || '').trim();

export const time = (d: Date) => d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
export const dayTime = (d: Date) =>
  d.toLocaleString([], {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
